import React, { useContext, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import { SimpleDialogContext } from '../../../../components/SimpleDialog'
import Button from '@mui/material/Button'
import api from '../../../../lib/api'
import { SimpleSnackbarContext } from '../../../../components/SimpleSnackbar'
import { SubtitleType } from '../../../../lib/types'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import CreateSubtitle from './CreateSubtitle'
import axios from 'axios'
import { capitalize } from '../../../../components/Capitalize'

interface iSubtitles {
  isEditor: boolean
  storyId: string
  initialSubtitles: SubtitleType[]
}

const Subtitles: React.FC<iSubtitles> = ({ isEditor, storyId, initialSubtitles }) => {
  const { actions: simpleDialogActions } = useContext(SimpleDialogContext)
  const { actions: snackActions } = useContext(SimpleSnackbarContext)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [subtitles, setSubtitles] = useState<SubtitleType[]>(initialSubtitles)

  useEffect(() => {
    setSubtitles(initialSubtitles)
  }, [initialSubtitles])

  const handleCreateFromTranscript = async () => {
    await api.createSubtitleFromTranscript(storyId)
    simpleDialogActions.open(
      'Subtitle Updated',
      'To see your changes reflected in the captions displayed in the video player, you will need to refresh the page and Select English Transcript in the video player caption control. Do you want to see your changes?',
      { okText: 'Yes, refresh the page now', cancelText: 'No, I will refresh later', showOk: true },
      success => {
        if (success) window.location.reload()
      }
    )
  }

  const handleDelete = async (subtitle?: SubtitleType) => {
    if (!subtitle) {
      snackActions.open('Something was wrong! please try again.')
      return
    }
    simpleDialogActions.open(
      'Delete subtitle',
      <>
        <Typography component="span">Are you sure want to delete the subtitle</Typography>
        <Typography fontWeight="700" component="span">{` "${subtitle.originalName || subtitle.name}" `}</Typography>
        <Typography component="span">from this story?</Typography>
      </>,
      { okText: 'Yes, delete', showOk: true, cancelText: 'Back' },
      async success => {
        try {
          if (success) {
            await api.deleteSubtitle(subtitle.id)
            setSubtitles(subtitles.filter(p => p.id !== subtitle.id))
            snackActions.open(`${subtitle.language} removed from this story!`)
            simpleDialogActions.open(
              'Subtitle Updated',
              'To see your changes reflected in the captions displayed in the video player, you will need to refresh the page and Select English Transcript in the video player caption control. Do you want to see your changes?',
              { okText: 'Yes, refresh the page now', cancelText: 'No, I will refresh later', showOk: true },
              success => {
                if (success) window.location.reload()
              }
            )
          }
        } catch (error) {
          // @ts-ignore
          const { data } = error.response
          if (data.code) {
            snackActions.open(`Error to add ${subtitle.language} to this story!`)
            return
          }
          snackActions.open('Something was wrong! please try again.')
        }
      }
    )
  }

  const handleCloseDialog = (subtitle?: SubtitleType) => {
    if (subtitle) {
      setSubtitles([...subtitles, subtitle])
      simpleDialogActions.open(
        'Subtitle Updated',
        'To see your changes reflected in the captions displayed in the video player, you will need to refresh the page and Select English Transcript in the video player caption control. Do you want to see your changes?',
        { okText: 'Yes, refresh the page now', cancelText: 'No, I will refresh later', showOk: true },
        success => {
          if (success) window.location.reload()
        }
      )
    }
    setIsOpen(false)
  }

  const downloadFileFromUrl = async (url: string, fileName: string): Promise<void> => {
    try {
      let downloadFilename: string
      if (fileName.endsWith('.vtt')) {
        downloadFilename = fileName
      } else if (fileName.endsWith('.txt')) {
        downloadFilename = fileName.replace('.txt', '.vtt')
      } else {
        downloadFilename = fileName + '.vtt'
      }

      const response = await axios.get(url, { responseType: 'blob' })
      const blob = new Blob([response.data])

      const blobUrl = window.URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = blobUrl
      a.download = downloadFilename
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(blobUrl)
      document.body.removeChild(a)
      snackActions.open('Subtitle downloaded successfully!')
    } catch (error) {
      console.error('Error downloading file:', error)
      snackActions.open('Ops! Something went wrong downloading the subtitle.')
    }
  }

  return (
    <Box
      component={Paper}
      sx={{
        width: '100%',
        display: 'flex',
        padding: 3,
        flexFlow: 'column'
      }}
    >
      {isEditor && (
        <>
          {isOpen && <CreateSubtitle storyId={storyId} onClose={handleCloseDialog} />}
          <Box>
            <Typography sx={{ marginBottom: 3 }} gutterBottom>
              Add a subtitle to enhance your story.
            </Typography>
            <Button variant="outlined" onClick={() => setIsOpen(true)} sx={{ mt: 1, mr: 1 }}>
              Create subtitle
            </Button>
            <Button variant="outlined" onClick={() => handleCreateFromTranscript()} sx={{ mt: 1, mr: 1 }}>
              Create subtitle from Trasncript
            </Button>
          </Box>
        </>
      )}
      <Divider sx={{ margin: '24px 0' }} />
      <Box
        sx={{
          marginBottom: 3
        }}
      >
        {subtitles.length ? (
          <Box sx={{ width: '100%', minHeight: 400 }}>
            <List>
              {subtitles.map(subtitle => (
                <ListItem
                  key={subtitle.id}
                  secondaryAction={
                    isEditor && (
                      <IconButton onClick={() => handleDelete(subtitle)} sx={{ color: 'white' }}>
                        <DeleteIcon />
                      </IconButton>
                    )
                  }
                >
                  <ListItemText
                    primary={
                      <Button onClick={() => downloadFileFromUrl(subtitle.url, capitalize(subtitle.language))}>
                        {capitalize(subtitle.language)}
                      </Button>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ) : (
          <Typography align="center">No subtitles here.</Typography>
        )}
      </Box>
    </Box>
  )
}

export default Subtitles
